/*******************************************************************************
 * index.js - Main Entry Point for BatchPayments Module System
 * 
 * IMPORTANT: Import order matters!
 * 1. globals.js must be imported first to set up window.* assignments
 * 2. Then operations.js and feature modules can access window.*
 ******************************************************************************/

// Step 1: Import globals FIRST - this sets up all window.* assignments
import './globals.js';

// Step 2: Import grid module - sets up grid function window.* assignments
import './grid.js';

// Step 3: Import API client with retry logic (must be before operations)
import './api-client.js';

// Step 3.5: Import dialogs module - sets up dialog handlers including missing data modal
import './dialogs.js';

// Step 4: Import operations module (reads from window.*)
import operations from './operations.js';

// Step 5: Import feature modules (read from window.*)
import resendRemittances from './features/resend-remittances.js';
import reRunReport from './features/rerun-report.js';

// Step 6: Import subscription handlers
import { subscribe, initWindowLoadHandler } from './subscriptions.js';

/*******************************************************************************
 * ADDITIONAL WINDOW ASSIGNMENTS
 ******************************************************************************/

// Expose subscribe function for backward compatibility
window.subscribe = subscribe;

// Initialize subscription window load handler
initWindowLoadHandler();

/*******************************************************************************
 * GLOBAL HELPER FUNCTIONS
 * 
 * These functions are called from Razor templates and other scripts
 ******************************************************************************/

// Window dimensions helper
window.getWindowDimensions = function() {
    return {
        width: window.innerWidth,
        height: window.innerHeight
    };
};

/*******************************************************************************
 * INITIAL DATA LOADING
 * 
 * Wait for Syncfusion grid to be instantiated, then load initial data
 ******************************************************************************/

/**
 * Polls until the Syncfusion Grid is instantiated
 * @returns {Promise} Resolves when grid is ready
 */
function waitForGrid() {
    return new Promise((resolve) => {
        const checkGrid = () => {
            const gridEl = document.getElementById('Grid');
            if (gridEl && gridEl.ej2_instances && gridEl.ej2_instances[0]) {
                resolve(gridEl.ej2_instances[0]);
            } else {
                setTimeout(checkGrid, 50);
            }
        };
        checkGrid();
    });
}

// Load initial data after DOM is ready and Syncfusion grid is instantiated
// Only runs on BatchPayments page (checks for page-specific element)
document.addEventListener('DOMContentLoaded', function() {
    // Check if we're on the BatchPayments page by looking for a unique element
    var customPager = document.getElementById("customPager");
    var invDateRangeFilter = document.getElementById("invDateRangeFilter");
    
    // Only proceed if we're on the BatchPayments page
    if (!customPager && !invDateRangeFilter) {
        console.log('Not on BatchPayments page, skipping invoice loading');
        return;
    }
    
    // Hide spinner
    var spinnerGlobal = document.getElementById("spinnerGlobal");
    if (spinnerGlobal) {
        spinnerGlobal.style.visibility = "hidden";
    }
    
    waitForGrid().then(function(grid) {
        console.log('Grid ready, loading initial data...');
        
        // Add keydown handler
        grid.addEventListener("keydown", window.keyDownHandler);
        
        // Set grid height
        grid.height = window.innerHeight;
        
        // Check Xero connection
        if (typeof window.CheckIfConnectedToXero === 'function') {
            window.CheckIfConnectedToXero();
        }
        
        // Get subscription ID for selection filtering
        var splitCode = window.getSubscriptionIDFromExpireOrganization(window.expireOrganization || '');
        
        // Load bills and restore selection
        if (typeof window.operations !== 'undefined' && typeof window.operations.onGetBills === 'function') {
            // Show spinner
            grid.showSpinner();
            if (spinnerGlobal) {
                spinnerGlobal.style.visibility = "visible";
            }
            
            window.operations.onGetBills(function() {
                // After bills loaded, restore selection from localStorage
                var storedData = window.gridSelection.getSelection(splitCode);
                window.storedData = storedData;
                
                if (storedData && storedData.length > 0) {
                    console.log("Stored data found, will attempt to update grid selection:", storedData.length, "items");
                    
                    var gridDataBoundHandler = function() {
                        grid.removeEventListener("dataBound", gridDataBoundHandler);
                        // Use a timeout to ensure grid is fully loaded
                        setTimeout(function() {
                            if (typeof window.updateGridSelectionAndData === 'function') {
                                window.updateGridSelectionAndData();
                            }
                        }, 100);
                    };
                    
                    grid.addEventListener("dataBound", gridDataBoundHandler);
                    if (grid.dataSource && grid.dataSource.length) {
                        grid.notify('dataBound', {});
                    } else {
                        grid.refresh();
                    }
                } else {
                    if (spinnerGlobal) {
                        spinnerGlobal.style.visibility = "hidden";
                    }
                    console.log("No stored data found for selection.");
                }
                
            });
        } else {
            console.error('operations.onGetBills not available');
        }
    });
});

console.log('BatchPayments ES6 module system initialized');
console.log('- operations:', typeof window.operations !== 'undefined' ? 'loaded' : 'NOT loaded');
console.log('- resendRemittances:', typeof window.resendRemittances !== 'undefined' ? 'loaded' : 'NOT loaded');
console.log('- reRunReport:', typeof window.reRunReport !== 'undefined' ? 'loaded' : 'NOT loaded');
